/**
 * Tracks Mapbox source loading progress for overlays and orthophotos.
 * Returns { total, loaded, isActive, isDone } for rendering a progress bar.
 *
 * Uses Mapbox `sourcedata` events to detect when each image/tile source
 * finishes loading. Follows the useTileStatusPoller pattern.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useProjectStore, useMapStore } from '../stores';

interface AssetLoadingState {
  total: number;
  loaded: number;
  isActive: boolean;
  isDone: boolean;
}

const INITIAL_STATE: AssetLoadingState = { total: 0, loaded: 0, isActive: false, isDone: false };
const DONE_DISPLAY_MS = 1500;
const SAFETY_TIMEOUT_MS = 30_000;

export default function useAssetLoadingProgress(): AssetLoadingState {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);

  const [state, setState] = useState<AssetLoadingState>(INITIAL_STATE);

  const expectedRef = useRef<Set<string>>(new Set());
  const loadedRef = useRef<Set<string>>(new Set());
  const doneTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Track whether we've already shown "done" for the current load cycle
  const completedRef = useRef(false);

  const updateState = useCallback(() => {
    const total = expectedRef.current.size;
    const loaded = Math.min(loadedRef.current.size, total);

    if (total === 0) {
      setState(INITIAL_STATE);
      return;
    }

    const allLoaded = loaded >= total;

    if (allLoaded && !completedRef.current) {
      completedRef.current = true;
      // Clear safety timer
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = undefined;
      }
      // Show "done" state briefly, then reset
      setState({ total, loaded: total, isActive: false, isDone: true });
      doneTimerRef.current = setTimeout(() => {
        setState(INITIAL_STATE);
      }, DONE_DISPLAY_MS);
    } else if (!allLoaded) {
      setState({ total, loaded, isActive: true, isDone: false });
    }
  }, []);

  // Compute expected source IDs from current project data
  const recompute = useCallback(() => {
    const { overlays, orthophotos } = useProjectStore.getState().data;
    const expected = new Set<string>();

    for (const o of overlays) {
      if (o.visible && o.url) expected.add(`overlay-${o.id}`);
    }
    for (const o of orthophotos) {
      if (o.visible && o.url) expected.add(`ortho-source-${o.id}`);
    }

    expectedRef.current = expected;

    // Check which sources are already loaded (browser cache)
    if (map) {
      for (const id of expected) {
        try {
          if (map.getSource(id) && map.isSourceLoaded(id)) {
            loadedRef.current.add(id);
          }
        } catch { /* source may not exist yet */ }
      }
    }

    updateState();

    // Start safety timeout if there are pending sources
    if (expected.size > 0 && loadedRef.current.size < expected.size) {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = setTimeout(() => {
        // Force-complete any remaining sources
        for (const id of expectedRef.current) {
          loadedRef.current.add(id);
        }
        updateState();
      }, SAFETY_TIMEOUT_MS);
    }
  }, [map, updateState]);

  // Listen to Mapbox sourcedata events + subscribe to project changes.
  // Combined into one effect to avoid race conditions between siteId reset
  // and subscribe callbacks (the old separate siteId effect would clear
  // expectedRef AFTER the subscribe callback had populated it).
  useEffect(() => {
    if (!map || !isMapReady) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (e: any) => {
      const sourceId = e.sourceId as string | undefined;
      if (sourceId && e.isSourceLoaded && expectedRef.current.has(sourceId)) {
        if (!loadedRef.current.has(sourceId)) {
          loadedRef.current.add(sourceId);
          updateState();
        }
      }
    };

    map.on('sourcedata', handler);

    // Initial compute
    loadedRef.current.clear();
    completedRef.current = false;
    recompute();

    // Track siteId so we can detect project switches
    let lastSiteId = useProjectStore.getState().meta.siteId;

    const unsub = useProjectStore.subscribe((state, prevState) => {
      const projectChanged = state.meta.siteId !== lastSiteId;
      const assetsChanged =
        state.data.overlays !== prevState.data.overlays ||
        state.data.orthophotos !== prevState.data.orthophotos;

      if (projectChanged) {
        lastSiteId = state.meta.siteId;
      }

      if (projectChanged || assetsChanged) {
        // Reset and recompute
        loadedRef.current.clear();
        completedRef.current = false;
        if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        recompute();
      }
    });

    return () => {
      map.off('sourcedata', handler);
      unsub();
    };
  }, [map, isMapReady, recompute, updateState]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  return state;
}
